import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { config } from '../../config';

export type AuthUser = {
  id: string;
  email?: string;
  role?: string;
  [key: string]: unknown;
};

type AuthResponse = {
  user: AuthUser;
};

type AuthError = {
  error: string;
  message?: string;
};

async function fetchWithCredentials<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error((data as AuthError).error || 'Request failed');
  }

  return data as T;
}

/**
 * Fetches the current authenticated user from the API.
 */
async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const data = await fetchWithCredentials<AuthResponse>('/auth/me');
    return data.user;
  } catch {
    return null;
  }
}

/**
 * Sign in with email and password.
 */
async function signIn(email: string, password: string): Promise<AuthUser> {
  const data = await fetchWithCredentials<AuthResponse>('/auth/signin', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return data.user;
}

/**
 * Sign up with email and password.
 */
async function signUp(
  email: string,
  password: string
): Promise<{ user: AuthUser; message: string }> {
  const data = await fetchWithCredentials<{
    user: AuthUser;
    message: string;
  }>('/auth/signup', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  return data;
}

/**
 * Send magic link to email.
 */
async function sendMagicLink(email: string): Promise<{ message: string }> {
  const data = await fetchWithCredentials<{ message: string }>('/auth/magic-link', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
  return data;
}

/**
 * Sign out and clear session.
 */
async function signOut(): Promise<void> {
  await fetchWithCredentials('/auth/signout', {
    method: 'POST',
  });
}

/**
 * Initiate OAuth sign in.
 * This redirects to the OAuth provider.
 */
function signInWithOAuth(provider: 'google' | 'apple' | 'azure') {
  const redirectTo = `${window.location.origin}`;
  window.location.href = `${config.apiUrl}/auth/oauth/${provider}?redirectTo=${encodeURIComponent(redirectTo)}`;
}

/**
 * Custom hook for authentication state and actions.
 */
export function useAuth() {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['auth', 'user'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  });

  const signInMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signIn(email, password),
    onSuccess: (user) => {
      queryClient.setQueryData(['auth', 'user'], user);
    },
  });

  const signUpMutation = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      signUp(email, password),
    onSuccess: (data) => {
      queryClient.setQueryData(['auth', 'user'], data.user);
    },
  });

  const magicLinkMutation = useMutation({
    mutationFn: ({ email }: { email: string }) => sendMagicLink(email),
  });

  const signOutMutation = useMutation({
    mutationFn: signOut,
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'user'], null);
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    refetch,
    signIn: signInMutation.mutateAsync,
    signInPending: signInMutation.isPending,
    signUp: signUpMutation.mutateAsync,
    signUpPending: signUpMutation.isPending,
    sendMagicLink: magicLinkMutation.mutateAsync,
    magicLinkPending: magicLinkMutation.isPending,
    magicLinkSuccess: magicLinkMutation.isSuccess,
    resetMagicLink: magicLinkMutation.reset,
    signOut: signOutMutation.mutateAsync,
    signOutPending: signOutMutation.isPending,
    signInWithOAuth,
  };
}
